import React, { useEffect } from 'react'
import ScreenWrapper from '~/components/screen_wrappers/screen_wrapper'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import {
  getIsPoliciesLoading,
  getPoliciesExpelled,
  getPoliciesUnexpelled
} from '@verzekeringapp-mono/shared/src/state/selectors/policies'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight'
import PolicySummaryRow from '~/components/PolicySummaryRow'
import MyPoliciesHeader from '~/components/MyPoliciesHeader'
import usePolicies from '@verzekeringapp-mono/shared/src/hooks/use_policies'
import useResourceAuthorization from '@verzekeringapp-mono/shared/src/hooks/use_resource_authorization'
import IllustrationPolicies from '~/components/illustrations/IllustrationPolicies'
import LoadingIndicator from '~/components/LoadingIndicator'
import { Link } from 'react-router-dom'

const PoliciesScreen: React.FC = () => {
  const { getPolicies } = usePolicies()
  const { getResourceAuthorization } = useResourceAuthorization()
  const policiesUnexpelled = useAppSelector(getPoliciesUnexpelled)
  const policiesExpelled = useAppSelector(getPoliciesExpelled)
  const [showExpelled, setShowExpelled] = React.useState(false)
  const isLoading = useAppSelector(getIsPoliciesLoading)

  useEffect(() => {
    getPolicies()
    getResourceAuthorization()
  }, [])

  return (
    <ScreenWrapper hasPadding={false}>
      <div className='flex items-start justify-between gap-8 px-4 pb-8 md:px-0'>
        <section
          aria-label='Mijn verzekeringen'
          className='min-w-0 w-full rounded-lg border border-border-subtle bg-surface-card p-6 lg:w-4/6 lg:shrink-0'
        >
          <MyPoliciesHeader />

          {isLoading && policiesUnexpelled.length === 0 && (
            <div
              role='status'
              aria-label='Verzekeringen laden'
              className='flex justify-center py-4'
            >
              <LoadingIndicator />
            </div>
          )}

          <ul className='divide-y divide-border-subtle border-y border-border-subtle'>
            {policiesUnexpelled.map(policy => (
              <li key={policy.id}>
                <PolicySummaryRow policy={policy} />
              </li>
            ))}
          </ul>

          {policiesExpelled.length > 0 && (
            <div className='mt-5'>
              <button
                type='button'
                onClick={() => setShowExpelled(!showExpelled)}
                aria-expanded={showExpelled}
                aria-controls='expelled-policies'
                className='flex w-full items-center justify-between gap-4 px-3 py-3 text-sm hover:bg-surface-hover focus-visible:bg-surface-hover'
              >
                {showExpelled ? 'Verberg beëindigde polissen' : 'Toon beëindigde polissen'}

                <FontAwesomeIcon
                  icon={['fal', showExpelled ? 'chevron-up' : 'chevron-down']}
                  className='text-icon-secondary'
                />
              </button>

              <ul
                id='expelled-policies'
                hidden={!showExpelled}
                className='divide-y divide-border-subtle border-y border-border-subtle'
              >
                {policiesExpelled.map(policy => (
                  <li key={policy.id}>
                    <PolicySummaryRow
                      policy={policy}
                      isUnexpelled={false}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Link
            className='mt-7 flex items-center justify-end gap-4 border-b border-border-subtle px-3 py-4 hover:bg-surface-hover focus-visible:bg-surface-hover'
            to='/verzekeringen/verzekeringsadvies'
          >
            <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-brand-subtle'>
              <FontAwesomeIcon
                icon='plus'
                className='text-icon-brand'
              />
            </div>

            <span className='text-sm underline'>Verzekering(en) toevoegen</span>

            <FontAwesomeIcon
              icon={faChevronRight}
              className='text-icon-secondary'
            />
          </Link>
        </section>

        <IllustrationPolicies className='hidden max-w-[40%] shrink text-icon-brand lg:block' />
      </div>
    </ScreenWrapper>
  )
}

export default PoliciesScreen
